import React, { useState, useEffect, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

import NoSearchResults from 'assets/no-search-results.svg'
import Box from 'components/Box'
import Button from 'components/Buttons/Button'
import CheckBox from 'components/Checkboxes/Checkbox'
import NoResults from 'components/NoResults'
import Text from 'components/Text'
import VirtualizedList, { VirtualizedListScrollEvent } from 'components/VirtualizedList'

import { searchAccount } from 'services/search'
import { SearchData } from 'services/types/search'
import { FETCHING_STATUS } from 'utils/constants'
import { useFilter } from 'queries/lookup'

import SearchBar from './SearchBar'
import SearchResultsLoader from './SearchResultsLoader'
import SearchResultsRow from './SearchResultRow'
import { StyledLinkFlex } from 'modules/shared/styles'

const PAGE_SIZE = 100

const GlobalSearch = () => {
  const { t } = useTranslation()

  const [searchText, setSearchText] = useState<string>('')
  const [showSearchResults, setShowSearchResults] = useState<boolean>(false)
  const [data, setData] = useState<Array<SearchData> | undefined>(undefined)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isLoadingNextPage, setIsLoadingNextPage] = useState<boolean>(false)
  const [resultCount, setResultCount] = useState<number>()
  const [resultCountDisplay, setResultCountDisplay] = useState<string>()
  const [selectedTypeFilters, setSelectedTypeFilters] = useState<Array<number>>([])
  const [selectedFieldFilters, setSelectedFieldFilters] = useState<Array<string>>([])

  const { data: filters } = useFilter({
    view: 'ALL',
  })

  const [leadTypeFilters, leadFieldFilters] = useMemo(() => {
    if (!filters) {
      return [[], []]
    }
    const {
      globalSearch: { leadTypes, fields },
    } = filters
    return [leadTypes, fields]
  }, [filters])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.keyCode === 13 && searchText != '') {
      !showSearchResults && setShowSearchResults(true)
      setIsLoading(true)
    }
  }

  const handleSearch = () => {
    if (searchText != '') {
      !showSearchResults && setShowSearchResults(true)
      setIsLoading(true)
    }
  }

  const handleTypeFilterChange = (id: number) => {
    if (selectedTypeFilters.includes(id)) {
      setSelectedTypeFilters(selectedTypeFilters.filter((i) => i !== id))
      setIsLoading(true)
      return
    }
    setSelectedTypeFilters([...selectedTypeFilters, id])
    setIsLoading(true)
  }

  const handleFieldFilterChange = (id: string) => {
    if (selectedFieldFilters.includes(id)) {
      setSelectedFieldFilters(selectedFieldFilters.filter((i) => i !== id))
      setIsLoading(true)
      return
    }
    setSelectedFieldFilters([...selectedFieldFilters, id])
    setIsLoading(true)
  }

  const handleResetFilters = () => {
    setSelectedTypeFilters([])
    setSelectedFieldFilters([])
    setIsLoading(true)
  }

  const closeSearchResults = () => {
    setShowSearchResults(false)
    setSearchText('')
    setSelectedTypeFilters([])
    setSelectedFieldFilters([])
  }

  const handleScroll: VirtualizedListScrollEvent = async (_e, isBottomReached) => {
    if (isBottomReached && !isLoadingNextPage && data && resultCount && data.length < resultCount) {
      setIsLoadingNextPage(true)

      const response = await searchAccount({
        query: searchText,
        last_record_id: data[data.length - 1].record_id,
        page_size: PAGE_SIZE,
        filters: {
          lead_type_id: selectedTypeFilters,
          field: selectedFieldFilters,
        },
      })

      if (response.status === FETCHING_STATUS.success) {
        setData([...data, ...response.data.data])
      }

      setIsLoadingNextPage(false)
    }
  }

  const getRow = (index: number) => {
    if (!data) return

    return (
      <>
        <SearchResultsRow rowIndex={index} searchTerm={searchText} data={data[index]} />
        {index === data.length - 1 && isLoadingNextPage && <SearchResultsLoader mt="24px" />}
      </>
    )
  }

  const fetchSearchResults = useCallback(async () => {
    const response = await searchAccount({
      query: searchText,
      last_record_id: null,
      page_size: PAGE_SIZE,
      filters: {
        lead_type_id: selectedTypeFilters,
        field: selectedFieldFilters,
      },
    })

    if (response.status === FETCHING_STATUS.success) {
      setData(response.data.data)
      setResultCount(response.data.num_result)
      setResultCountDisplay(response.data.num_result_display)
    }

    setIsLoading(false)
  }, [searchText, selectedFieldFilters, selectedTypeFilters])

  useEffect(() => {
    if (!isLoading) return

    fetchSearchResults()
  }, [fetchSearchResults, isLoading])

  return (
    <>
      <SearchBar
        value={searchText}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchText(e.target.value)}
        onKeyDown={handleKeyDown}
        onClose={closeSearchResults}
        onSearch={handleSearch}
      />
      {showSearchResults && (
        <SearchResults width="785px">
          <ResultsSection width="544px">
            {isLoading ? (
              <SearchResultsLoader />
            ) : (
              <ResultContainer>
                <ResultCountText height="34px">
                  {t('search_result_count', { count: resultCountDisplay ?? resultCount })}
                </ResultCountText>
                {data && !data.length ? (
                  <StyledNoResults
                    icon={<NoSearchResults />}
                    message="search_empty_lbl"
                    stacking="invertColumn"
                    addOnAfter={
                      <NewLeadLink to={{ pathname: '/newlead' }}>
                        <Text fontWeight="medium" textDecoration="none">
                          lead_search_add_new_lead
                        </Text>
                      </NewLeadLink>
                    }
                  />
                ) : (
                  data && <VirtualizedList rowCount={data.length} rowGetter={getRow} onScroll={handleScroll} />
                )}
              </ResultContainer>
            )}
          </ResultsSection>
          <FiltersSection width="241px">
            <Box height="40px" display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
              <Text fontWeight="medium">filters_lbl</Text>
              <Button variant="ghost" onClick={handleResetFilters}>
                reset_filters_btn
              </Button>
            </Box>
            <Box mt="16px" mb="30px">
              <Text mb="8px" fontWeight="medium">
                filter_by_type_lbl
              </Text>
              {leadTypeFilters.map(({ id, name }) => {
                const typeId = parseInt(id)
                return (
                  <CheckBox
                    key={`type-filter-${typeId}`}
                    mb="8px"
                    checked={selectedTypeFilters.includes(typeId)}
                    onChange={() => {
                      handleTypeFilterChange(typeId)
                    }}
                  >
                    <Text ml="8px">{name}</Text>
                  </CheckBox>
                )
              })}
            </Box>
            <Box>
              <Text mb="8px" fontWeight="medium">
                filter_by_fields_lbl
              </Text>
              {leadFieldFilters &&
                leadFieldFilters.map(({ id }) => (
                  <CheckBox
                    key={`field-filter-${id}`}
                    mb="8px"
                    checked={selectedFieldFilters.includes(id)}
                    onChange={() => {
                      handleFieldFilterChange(id)
                    }}
                  >
                    <Text ml="8px">{t(`${id?.toLowerCase()}_lbl`)}</Text>
                  </CheckBox>
                ))}
            </Box>
          </FiltersSection>
        </SearchResults>
      )}
    </>
  )
}

const SearchResults = styled(Box)`
  display: flex;
  flex-direction: row;
  position: absolute;
  height: calc(100vh - 60px);
  right: 0;
  top: 60px;
  border-radius: 4px;
  box-shadow: 0 0 12px 1px rgba(0, 0, 0, 0.2);
  background-color: white;
`

const ResultsSection = styled(Box)`
  border-right: 1px solid ${(p) => p.theme.colors.gray3};
`

const FiltersSection = styled(Box)`
  padding: 0 21px 21px;
`

const ResultCountText = styled(Text)`
  display: flex;
  align-items: center;
  background-color: ${(p) => p.theme.colors.gray2};
  padding-left: 10px;
  font-weight: 500;
`

const StyledNoResults = styled(NoResults)`
  height: 100%;
  background-image: unset;
  background-color: white;
`

const ResultContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
`

const NewLeadLink = styled(StyledLinkFlex)`
  justify-content: center;
  align-items: center;
  margin-top: 16px;
`

export default GlobalSearch
